import { type PowerSyncDrizzleDB, user } from "@bodega/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── `user` is NOT excluded from `patchRowVerified`—audit 2026-08-13,
 * updated the same day after the `ThemeProvider` seeding guard ──
 *
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1 assumed that `user`
 * could be excluded because "the server creates the row at registration, so
 * it is already in the checkpoint before the first preference." Verified in
 * THIS repo and it is FALSE—and remains so even if `ThemeProvider` is no longer
 * part of the problem (see below):
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as the local
 *     SQLite is opened, not when the initial sync finishes (`connect()` is async and
 *     posterior a abrir el SQLite local).
 *   - `userId()` becomes active as soon as the login flow receives the session from the
 *     server—a REST call independent of the PowerSync stream.
 *   - There is no `ensureUser*` / transactional safety net in this
 *     repo (`git grep -n 'ensureUser'` sin resultados).
 *
 * These two points alone are enough: between `db` + `userId()` becoming
 * ready and the initial checkpoint bringing the `user` row created by the
 * server, there is a window in which a normal `UPDATE` (changing language,
 * theme…) targets a row the local view does not yet have—it matches ZERO
 * rows and silently returns "success." This window does not depend on anyone
 * seeding a local row: it is simply the view synchronizing.
 *
 * (Historical context, now CLOSED) Until today's guard, a third
 * factor aggravated this same window for real accounts: the
 * `createEffect` de `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) insertaba localmente una fila
 * `user` "ghost" row of 3 columns for ANY `uid`, without distinguishing
 * a guest from a real account, competing with the real row the server had already
 * created (`packages/core-rust/src/db/user.rs:101,350`) and that was on
 * its way through sync. The `uid !== VISITOR_ID` guard in that `createEffect` closed
 * that specific root cause. **This does NOT make the `patchRowVerified` protection
 * for `user` unnecessary**—the two points above (`db` and
 * `userId()` ready before the checkpoint) still open the same window
 * for any early write, with or without a ghost row in the way.
 * Here `patchRowVerified` is generic checkpoint defense in depth,
 * not a patch left stuck to the now-closed bug—if it is ever removed,
 * it should be because the window above is proven not to exist, not
 * because `ThemeProvider` stopped seeding ghosts.
 *
 * `patchRowVerified` writes → rereads → retries → throws instead of silently losing
 * preference silently. Guarded by
 * `mutations-verified-write.test.ts` (the window, including its CONTROL block) and
 * by `theme-provider-seed-window.test.ts` (the closed root cause, retained
 * as evidence of the measurement).
 *
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ──
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this form:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * In other words, in guest mode the UPDATE went out **without `WHERE`** → it rewrote ALL
 * `user` rows in the local database. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for guests reads with `.limit(1)` and no
 * filter; however, a read `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the former picks the wrong row, while the latter crushes them all.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during a guest→real upgrade, the
 * guest database CONNECTS to the real account connector and
 * auto-subscribes to every stream—including `organizations`, which synchronizes
 * one `user` row for EACH co-member of that account's organizations.
 * There, the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each
 * touched row queues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid`
 * is exactly `VISITOR_ID`. Thus, `eq(user.id, userId)` points to the SAME
 * row that the unfiltered branch intended to touch when the table had only one
 * row—identical behavior in the normal case and bounded in the rest. It is also
 * the form already used by `updateUserFontFamilyMutation`, which never had
 * a guest branch.
 *
 * Since `patchRowVerified`, the `userId` filter no longer depends on each
 * mutation writing it manually: `idAndScopeCondition` ALWAYS applies
 * `eq(idColumn, id)` at minimum, so an UPDATE without `WHERE` on `user`
 * is no longer a form the code can take by accident.
 *
 * Guardado por `packages/shared/src/users/__tests__/mutations-where.test.ts`
 * (correct WHERE) and `mutations-verified-write.test.ts` (the window of
 * the checkpoint, including its CONTROL block).
 /** Waits for the app to finish booting (skeleton overlay disappears). */

/**
 * Shared Drizzle mutation for updating User locale.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
